import React, { useState, useEffect, useRef } from 'react';
import KickImage from 'Reuseables/KickImage';
import { connect } from 'react-redux';
import { InputSelect } from '../../Forms/Input';

// ========= action import section ========= //
import {
  doFetchUserBankAccount,
  getBankList,
  saveRekening,
} from '../../../actions/postalCodeAction';
import {
  getOtp,
  resetOtpState,
  getOtpStatus
} from '../../../actions/authAction';
// ====== end of action import section ====== //

// ========= interface import section ========= //
import AuthInterface, {
  UserDataInterface,
} from '../../../interface/reducers/auth';
import DataPostalCodeInterface, {
  UserBankAccountStateInterface,
} from '../../../interface/reducers/postalCode';
// ====== end of interface import section ====== //

import { useOutsideAlerter, useOtpStorage } from '../../../helpers/dom';
import { IconContext } from 'react-icons';
import { GrFormDown } from 'react-icons/gr';
import ModalPasswordV3 from '../../Modals/ModalPasswordV3';
import { ModalOtp } from '../../Modals';
import { toast } from 'react-toastify';

type stateToProps = {
  dataAuth: AuthInterface;
  dataPostalCode: DataPostalCodeInterface;
};

interface BankAccountInterface {
  // state to props
  userData: UserDataInterface;
  dataBankList: any;
  otpCode: any;
  dataUserBankAccount: UserBankAccountStateInterface;
  updatedUserBankAccount: UserBankAccountStateInterface;
  // dispatch to props
  fetchUserBank: () => Promise<any>;
  getOtp: () => Promise<any>;
  resetOtpState: () => Promise<any>;
  fetchBankList: () => Promise<any>;
  doSaveRekening: (dataUserBankAccount: any, payload: any) => Promise<any>;
}

/**
 * Bank Account Tab on Setting User Dashboard section for Kick Avenue 3.0 Website.
 * ===============================================
 * @param {BankAccountInterface} props for this component.
 * @return {JSX.Element}
 */
function BankAccountTab({
  dataBankList,
  fetchBankList,
  userData,
  dataUserBankAccount,
  updatedUserBankAccount,
  doSaveRekening,
  fetchUserBank,
  getOtp,
  otpCode,
  resetOtpState,
  getOtpStatus,
  otpStatus
}: BankAccountInterface): JSX.Element {
  const { saveToLocalStorage, checkIfExpired, resendOtp, resetOtpFromLocalstorage } = useOtpStorage();
  const [isOtpActive, setIsOtpActive] = useState(false)

  const [firstRender, setFirstRender] = useState(false);
  const wrapperRefBank = useRef(null);
  const [isBankOpened, setBankOpened] = useState(false);
  useOutsideAlerter(wrapperRefBank, () => setBankOpened(false));
  const [isPasswordOpened, setPasswordOpened] = useState(false);
  const togglePassword = () => setPasswordOpened(!isPasswordOpened);

  const [name, setName] = useState('');
  const [number, setNumber] = useState('');
  const [bankInput, setBankInput] = useState('');
  const [bankName, setBankName] = useState({ label: '' });
  const [branch, setBranch] = useState('');
  const [errorPassword, setErrorPassword] = useState('');
  const [showModalOtp, setShowModalOtp] = useState(false);
  const [gettingOtp, setGettingOtp] = useState(false);
  const [otpErrorMsg, setOtpErrorMsg] = useState("");
  const [currentProcess, setCurrentProcess] = useState("");
  const [passwordPayload, setPasswordPayload] = useState("");

  const [bankList, setBankList] = useState([
    { value: '', label: 'Select Bank Name' },
  ]);
  const [oldBankList, setOldBankList] = useState([
    { value: '', label: 'Select Bank Name' },
  ]);
  const [disabledButton, setDisabledButton] = useState(true);

  const getOtpHandler = () => {
    if (checkIfExpired()) {
      getOtp()
    } else {
      setOtpErrorMsg("")
      setGettingOtp(false)
      setShowModalOtp(true)
    }
  }

  const onChangeName = (e) => setName(e.target.value);
  const onChangeNumber = (e) => setNumber(e.target.value);
  const onChangeBranch = (e) => setBranch(e.target.value);
  const onChangeBankInput = (e) => {
    setBankInput(e.target.value);
    const newBankList = oldBankList.filter((t) =>
      t.label.toLowerCase().includes(e.target.value.toLowerCase()),
    );
    setBankList(newBankList);
  };

  useEffect(() => {
    fetchBankList();
    fetchUserBank().catch(() => {
      setName('');
      setNumber('');
      setBranch('');
      setBankInput('');
      setBankName({ label: '' });
    });
    setFirstRender(true);
    getOtpStatus()
  }, []);

  useEffect(() => {
    const { data } = dataUserBankAccount;
    if (data.length) {
      setName(data[0].account_name);
      setNumber(data[0].account_number);
      setBranch(data[0].branch);
    }
    if (data.length === 0) {
      setName('');
      setNumber('');
      setBranch('');
    }
  }, [dataUserBankAccount.data]);

  useEffect(() => {
    if (!dataBankList.isFetch && dataBankList.status_code === 200) {
      const bankListOptions = dataBankList.data.map((item) => ({
        value: item.code,
        label: item.name,
      }));
      const { data } = dataUserBankAccount;
      if (data.length && data[0].hasOwnProperty('bank_name')) {
        const userBankName = bankListOptions.find(
          (item) => item.label === data[0].bank_name,
        );
        if (userBankName) {
          setBankName(userBankName);
          setBankInput(userBankName.label);
        } else {
          setBankName({ label: '' });
          setBankInput('');
        }
      } else {
        setBankName({ label: '' });
      }
      setBankList(bankListOptions);
      setOldBankList(bankListOptions);
    }
  }, [dataBankList, dataUserBankAccount.data]);

  useEffect(() => {
    if (firstRender) {
      const bankListOptions = dataBankList.data.map((item) => ({
        value: item.code,
        label: item.name,
      }));
      let userBankName = {
        label: '',
      };
      if (
        dataUserBankAccount.data.length &&
        dataUserBankAccount.data[0].hasOwnProperty('bank_name') &&
        dataUserBankAccount.data[0].bank_name
      ) {
        userBankName = bankListOptions.find(
          (item) => item.label === dataUserBankAccount.data[0].bank_name,
        );
        if (!userBankName) {
          userBankName = { label: '' };
        }
      }

      if (
        name === '' ||
        number === '' ||
        (bankName.hasOwnProperty('label') && bankName.label === '') ||
        (dataUserBankAccount.data.length &&
          name === dataUserBankAccount.data[0].account_name &&
          number === dataUserBankAccount.data[0].account_number &&
          bankName.hasOwnProperty('label') &&
          bankName.label === userBankName.label &&
          branch === dataUserBankAccount.data[0].branch)
      ) {
        setDisabledButton(true);
      } else {
        setDisabledButton(false);
      }
    }
  }, [name, number, bankName, branch]);

  useEffect(() => {
    if (userData.roles.filter((t) => t === 'unverified_user').length) {
      setDisabledButton(true);
    }
  }, [userData]);

  useEffect(() => {
    if(otpStatus?.data?.change_bank_account){
      setIsOtpActive(otpStatus.data.change_bank_account)
    } else {
      setIsOtpActive(false)
    }
  }, [otpStatus?.data?.change_bank_account])

  useEffect(() => {
    if (otpCode.success) {
      setOtpErrorMsg("")
      setGettingOtp(false)
      setShowModalOtp(true)
      saveToLocalStorage()
    } else if (otpCode?.message) {
      setOtpErrorMsg(otpCode?.message)
      setGettingOtp(false)
      setShowModalOtp(true)
    } else {
      setOtpErrorMsg("")
    }
  }, [otpCode])

  useEffect(() => {
    if (isOtpActive) {
      if (updatedUserBankAccount?.error) {
        if (updatedUserBankAccount?.error?.errors) {
          const errors = Object.values(updatedUserBankAccount?.error?.errors)
          if (Array.isArray(errors) && errors.length > 0) {
            setOtpErrorMsg(errors[0])
          }
        } else {
          setOtpErrorMsg(updatedUserBankAccount?.error?.message)
        }
        setGettingOtp(false)
      } else {
        setOtpErrorMsg("")
      }
    }
  }, [updatedUserBankAccount])

  const onSubmit = (otp) => {
    setErrorPassword('');
    setCurrentProcess("")
    const payload = {
      account_name: name,
      account_number: number,
      bank_name: bankName ? bankName.label : '',
      branch: branch,
      password: passwordPayload
    };

    if (otp) {
      payload.otp = otp
    }

    doSaveRekening(dataUserBankAccount, payload)
      .then(() => {
        setOtpErrorMsg("")
        setCurrentProcess("bank-account-success")
        setPasswordOpened(false)
        resetOtpFromLocalstorage()
        toast.success('Success updating your bank account.', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: false,
          progress: undefined,
        });
      })
      .catch((e) => {
        let errMsg =
          typeof e === 'string'
            ? e
            : e.response?.data?.error?.errors?.password
              ? e.response.data.error.errors.password[0]
              : e.response?.data?.error?.errors?.account_number
                ? e.response.data.error.errors.account_number[0]
                : e.response.data.error?.message
                  ? e.response.data.error.message
                  : 'Sorry, we have a problem!. Please inform our friendly staff via Whatsapp +6281210005425';

        setErrorPassword(errMsg);
        // toast.error(

        //   {
        //     position: 'top-right',
        //     autoClose: 5000,
        //     closeOnClick: true,
        //     pauseOnHover: true,
        //   },
        // );
      });
  };

  useEffect(() => {
    setCurrentProcess("")
  }, [showModalOtp])

  return (
    <div className={'bank-account-container'}>
      <div className={'form-parent'}>
        <p>Account Holder’s Name</p>
        <input
          placeholder={'Input your Account Holder Name'}
          value={name}
          onChange={onChangeName}
        />
      </div>
      <div className={'form-parent'}>
        <p>Account Number</p>
        <input
          placeholder={'Input your Account Number'}
          value={number}
          onChange={onChangeNumber}
        />
      </div>
      <div className={'form-parent'}>
        <p>Bank Name</p>
        <div
          className={'text-box'}
          onClick={() => (bankList.length ? setBankOpened(true) : null)}
        >
          {/* <p>{bankName && bankName.label ? bankName.label : 'Select Bank'}</p> */}
          <input
            placeholder={'Select Bank'}
            value={bankInput}
            onChange={onChangeBankInput}
          />
          <IconContext.Provider value={{ color: '#242424', size: '15px' }}>
            <div className={'icon-dropdown'}>
              <GrFormDown />
            </div>
          </IconContext.Provider>
        </div>
        {isBankOpened && (
          <div
            ref={wrapperRefBank}
            className={'search-product-result-container'}
          >
            {bankList.map((t) => {
              const onClick = () => {
                setBankName(t);
                setBankOpened(false);
                setBankInput(t.label);
                setBankList(oldBankList);
              };
              return (
                <div
                  className={`search-bank-item${t === bankName ? ' selected' : ''
                    }`}
                  onClick={onClick}
                  key={t.value}
                >
                  <p>{t.label}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <div className={'form-parent'}>
        <p>Branch</p>
        <input
          placeholder={'Input your Branch'}
          value={branch}
          onChange={onChangeBranch}
        />
      </div>
      <div
        className={`button-submit ${disabledButton ? 'disabled' : ''}`}
        onClick={() => {
          if (!disabledButton) {
            setErrorPassword('');
            setPasswordOpened(true);
          }
        }}
      >
        <p>Save Changes</p>
      </div>

      {showModalOtp ? <ModalOtp
        show={showModalOtp}
        onHide={() => {
          setShowModalOtp(false)
          resetOtpState()
          setPasswordOpened(false)
        }}
        email={userData?.email ?? ""}
        submitOtp={(otp) => {
          onSubmit(otp)
          setGettingOtp(true)
        }}
        resendCode={() => {
          getOtp()
          setGettingOtp(true)
          resendOtp()
        }}
        gettingOtp={gettingOtp}
        errorMsg={otpErrorMsg}
        currentProcess={currentProcess}
        backAction={() => {
          setShowModalOtp(false)
          setPasswordOpened(true)
          resetOtpState()
        }}
      /> : null}
      {isPasswordOpened ? <ModalPasswordV3
        titleModal={'Save Bank Account'}
        isSubmit={updatedUserBankAccount.isFetch}
        errorMessage={errorPassword}
        isOpen={isPasswordOpened}
        onToggle={togglePassword}
        onClick={(e) => {
          if (isOtpActive) {
            setPasswordPayload(e)
            getOtpHandler()
            togglePassword()
            setCurrentProcess("")
          } else {
            setPasswordPayload(e)
            onSubmit(null)
          }
        }}
        hasPassword={userData.have_password}
      /> : null}
    </div>
  );
}

const mapStateToProps = (state: stateToProps) => {
  return {
    userData: state.dataAuth.userdata,
    dataBankList: state.dataPostalCode.bankList,
    dataUserBankAccount: state.dataPostalCode.userBankAccount,
    updatedUserBankAccount: state.dataPostalCode.updatedUserBankAccount,
    otpCode: state.dataAuth.otpCode,
    otpStatus: state.dataAuth.otpStatus
  };
};

const mapDispatchToProps = (dispatch: (arg0: any) => any) => {
  return {
    fetchBankList: () => dispatch(getBankList()),
    fetchUserBank: () => dispatch(doFetchUserBankAccount()),
    getOtp: () => dispatch(getOtp()),
    resetOtpState: () => dispatch(resetOtpState()),
    getOtpStatus: () => dispatch(getOtpStatus()),
    doSaveRekening: (dataUserBankAccount: any, payload: any) =>
      dispatch(saveRekening(dataUserBankAccount, payload)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(BankAccountTab);
